import {
  useState,
  useEffect,
  useContext,
  useMemo,
  useRef,
  type Dispatch,
  type RefObject,
  type SetStateAction,
} from "react";
import { BrandContext } from "../../../../contexts/BrandContext/context";
import { brandService } from "../../../../services/brand.service";
import { Button } from "../../../../components/ui/button";
import { ShowcaseImagesManager } from "../../../../components/ShowcaseImagesManager";
import { ProductImageSelector } from "../../../../components/ProductImageSelector";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../../components/ui/card";
import { Label } from "../../../../components/ui/label";
import { Separator } from "../../../../components/ui/separator";
import {
  Eye,
  Type,
  Users,
  Save,
  RotateCcw,
  Smartphone,
  Monitor,
  Info,
  ChevronDown,
} from "react-feather";
import { toast } from "react-hot-toast";
import { getRichTextLimitError } from "@/utils/richText";
import { BrandDetail } from "../../BrandDetail";
import {
  ICON_SIZES,
  AppearanceSettings,
  THEME_CONFIGS,
  BrandDetails,
} from "@/types/brand";
import { Input } from "@/components/ui/input";
import { productService } from "@/services/product.service";
import { Product } from "@/types/campaign";
import { useAppearanceHighlight } from "@/hooks/useAppearanceHighlight";
import { DEFAULT_BRAND } from "@/theme/brandTheme";
import { RichTextEditor, RichTextEditorRef } from "@/components/Editor/RichTextEditor";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { PortalBrandingSection } from "./PortalBrandingSection";
import { BannerFramingCard } from "./BannerFraming/BannerFramingCard";
import {
  CUSTOM_JOIN_PAGE_QUERY_KEY,
  useCustomJoinPageState,
} from "./CustomJoinPage/useCustomJoinPageState";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { customJoinPageService } from "@/services/customJoinPage.service";
import { ConfirmationModal } from "@/components/ui/confirmation-modal";

export default function AppearanceSettingsComponent() {
  const { brandDetails, refetchBrandDetails } = useContext(BrandContext);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Once the Reveshare team enables full customization the brand chooses
  // which join page is live. While the custom page is live the templated
  // join portal below doesn't apply, so its editor steps aside (the join copy
  // stays editable: the custom page can read it through tokens).
  const { data: customJoinPage } = useCustomJoinPageState();
  const customPageEnabled = Boolean(customJoinPage?.enabled);
  const customPageLive = Boolean(customJoinPage?.isLive);
  const [pendingLive, setPendingLive] = useState<boolean | null>(null);
  const [switching, setSwitching] = useState(false);

  const applyLive = async () => {
    if (pendingLive === null) return;
    setSwitching(true);
    try {
      await customJoinPageService.setLive(pendingLive);
      await queryClient.invalidateQueries({ queryKey: CUSTOM_JOIN_PAGE_QUERY_KEY });
      toast.success(
        pendingLive ? "Your custom page is live" : "Back to the standard join page"
      );
    } catch (e) {
      const message = e instanceof Error ? e.message : "Couldn't switch";
      toast.error(message);
      // Nothing published yet: take them to where they can fix that.
      if (/publish/i.test(message)) navigate("/brand/settings?tab=custom-page");
    } finally {
      setSwitching(false);
      setPendingLive(null);
    }
  };
  const [isLoading, setIsLoading] = useState(false);
  const [showPreview, _] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);
  const [viewMode, setViewMode] = useState<"mobile" | "desktop">("mobile");
  // Collapsible sections: white-label (brand colors) and join portal
  const [whiteLabelOpen, setWhiteLabelOpen] = useState(true);
  const [joinPortalOpen, setJoinPortalOpen] = useState(true);
  const joinTextEditorRef = useRef<RichTextEditorRef>(null);
  const ambassadorTextEditorRef = useRef<RichTextEditorRef>(null);

  const [settings, setSettings] = useState<AppearanceSettings>({
    joinText: "",
    joinTitle: "",
    ambassadorText: "",
    theme: "TYPE_1",
    socialMediaIconSizes: "Medium",
    primayColour: DEFAULT_BRAND.primary,
    secondaryColour: DEFAULT_BRAND.secondary,
    imageIds: [],
    showcaseProductIds: [],
  });

  const [originalSettings, setOriginalSettings] =
    useState<AppearanceSettings | null>(null);

  // Track changed fields for highlighting (used in desktop preview)
  const changedFields = useAppearanceHighlight(settings, originalSettings);

  // Ref for mobile preview iframe to send postMessage updates
  const mobilePreviewIframeRef = useRef<HTMLIFrameElement>(null);

  // Initialize settings when brand details are available

  const [products, setProducts] = useState<Product[]>([]);

  useEffect(() => {
    const fetchProducts = async () => {
      const response = await productService.findAll();
      setProducts(response.products);
    };
    fetchProducts();
  }, []);

  useEffect(() => {
    if (brandDetails) {
      // The brand-wide join copy lives on brandAppearance. The fallback to the
      // default program is transitional — it keeps this form populated for
      // brands whose data predates the backfill that moved the copy across.
      const appearance = (brandDetails as BrandDetails).brandAppearance;
      const defaultProgram = (brandDetails as BrandDetails).program;
      const initialSettings: AppearanceSettings = {
        joinText: appearance?.joinText || defaultProgram?.joinText || "",
        joinTitle: appearance?.joinTitle || defaultProgram?.joinTitle || "",
        ambassadorText:
          appearance?.ambassadorText || defaultProgram?.ambassadorText || "",
        theme:
          (brandDetails as BrandDetails).brandAppearance?.theme || "TYPE_1",
        socialMediaIconSizes:
          (brandDetails as BrandDetails).brandAppearance
            ?.socialMediaIconSizes || "Medium",
        primayColour:
          (brandDetails as BrandDetails).brandAppearance?.primayColour ||
          DEFAULT_BRAND.primary,
        secondaryColour:
          (brandDetails as BrandDetails).brandAppearance?.secondaryColour ||
          DEFAULT_BRAND.secondary,
        imageIds: (brandDetails as BrandDetails).brandAppearance?.imageIds || [
          "https://mxlvkkghmywfwlizcoch.supabase.co/storage/v1/object/public/arena-images/company-banners/1756854039535-0.6546327944881837.jpg",
          "https://mxlvkkghmywfwlizcoch.supabase.co/storage/v1/object/public/arena-images/company-logos/1756854037832-0.7031716617277495.png",
        ],
        showcaseProductIds:
          (brandDetails as BrandDetails).brandAppearance?.showcaseProductIds ||
          [],
      };

      setSettings(initialSettings);
      setOriginalSettings(initialSettings);
    }
  }, [brandDetails]);

  // Check for changes
  useEffect(() => {
    if (originalSettings) {
      const hasChanges =
        JSON.stringify(settings) !== JSON.stringify(originalSettings);
      setHasChanges(hasChanges);
    }
  }, [settings, originalSettings]);

  const handleSettingChange = (
    key: keyof AppearanceSettings,
    value: string
  ) => {
    setSettings((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSave = async () => {
    if (!brandDetails) return;

    const joinTextError = getRichTextLimitError(settings.joinText || "", 4000);
    if (joinTextError) {
      toast.error(joinTextError);
      return;
    }

    const ambassadorTextError = getRichTextLimitError(
      settings.ambassadorText || "",
      4000
    );
    if (ambassadorTextError) {
      toast.error(ambassadorTextError);
      return;
    }

    setIsLoading(true);
    try {
      // Save to backend
      await brandService.updateAppearanceSettings(settings);

      // Refetch brand details to get the latest data from the backend
      // This will trigger the useEffect to re-initialize settings from the updated brandDetails
      await refetchBrandDetails();

      toast.success("Appearance settings saved successfully!");
    } catch (error) {
      toast.error("Failed to save appearance settings");
      console.error("Save error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    if (originalSettings) {
      setSettings(originalSettings);
      setHasChanges(false);
    }
  };

  const getCurrentTheme = () =>
    THEME_CONFIGS[settings.theme as keyof typeof THEME_CONFIGS];

  const currentTheme = getCurrentTheme();

  // Build the iframe URL from the LAST-SAVED settings, and only once they
  // exist. This memo used to read the live draft while depending only on the
  // username: on the first render the draft still held the defaults, so the
  // iframe loaded with defaults baked into its query string and the memo never
  // recomputed — the preview showed a default join page until the first edit.
  // `originalSettings` is the right source: correct on initial load, stable
  // while typing (a draft dependency would reload the iframe per keystroke).
  // Live edits stream over postMessage instead.
  const previewUrl = useMemo(() => {
    if (!brandDetails?.username || !originalSettings) return "";

    const baseUrl = `/brand/appearance-preview/${brandDetails.username}`;
    const params = new URLSearchParams();

    // Add all appearance settings as query params
    if (originalSettings.joinText)
      params.set("joinText", originalSettings.joinText);
    if (originalSettings.joinTitle)
      params.set("joinTitle", originalSettings.joinTitle);
    if (originalSettings.ambassadorText)
      params.set("ambassadorText", originalSettings.ambassadorText);
    if (originalSettings.theme) params.set("theme", originalSettings.theme);
    if (originalSettings.socialMediaIconSizes)
      params.set("socialMediaIconSizes", originalSettings.socialMediaIconSizes);
    if (originalSettings.primayColour)
      params.set("primayColour", originalSettings.primayColour);
    if (originalSettings.secondaryColour)
      params.set("secondaryColour", originalSettings.secondaryColour);
    if (originalSettings.imageIds && originalSettings.imageIds.length > 0) {
      params.set("imageIds", JSON.stringify(originalSettings.imageIds));
    }
    if (
      originalSettings.showcaseProductIds &&
      originalSettings.showcaseProductIds.length > 0
    ) {
      params.set(
        "showcaseProductIds",
        JSON.stringify(originalSettings.showcaseProductIds)
      );
    }

    return `${baseUrl}?${params.toString()}`;
  }, [brandDetails?.username, originalSettings]);

  // Latest draft, readable from the mount-once message listener below.
  const settingsRef = useRef(settings);
  settingsRef.current = settings;

  // The preview app announces itself once its message listener is attached;
  // reply with the current draft. This replaces the old timer-based initial
  // send: the iframe is an entire SPA that takes far longer than any fixed
  // delay to boot, so messages posted on a 100ms guess (or on the iframe's
  // `load` event, which fires before React mounts the listener) were simply
  // lost, and the preview sat on stale values until the next edit.
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== "APPEARANCE_PREVIEW_READY") return;
      mobilePreviewIframeRef.current?.contentWindow?.postMessage(
        {
          type: "APPEARANCE_SETTINGS_UPDATE",
          settings: settingsRef.current,
        },
        window.location.origin
      );
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  // Stream draft edits into the already-booted iframe without reloading it.
  // Messages sent before the iframe is ready are dropped — the ready
  // handshake above delivers the current draft the moment it can listen.
  useEffect(() => {
    mobilePreviewIframeRef.current?.contentWindow?.postMessage(
      {
        type: "APPEARANCE_SETTINGS_UPDATE",
        settings: settings,
      },
      window.location.origin
    );
  }, [settings]);

  return (
    <div className="space-y-6 h-full">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground">
            Customizations
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            {customPageLive
              ? "White-label your brand colors — your live join page is the custom one from the Custom page tab"
              : "White-label your brand colors and customize how your join portal looks to potential affiliates"}
          </p>
        </div>

        <div className="flex gap-3">
          {/* <Button
            variant="outline"
            onClick={() => setShowPreview(!showPreview)}
            className="flex items-center gap-2"
          >
            <Eye className="w-4 h-4" />
            {showPreview ? "Hide Preview" : "Show Preview"}
          </Button> */}

          {hasChanges && (
            <>
              <Button
                variant="outline"
                onClick={handleReset}
                className="flex items-center gap-2"
              >
                <RotateCcw className="w-4 h-4" />
                Reset
              </Button>

              <Button
                id="AS-save-button"
                onClick={handleSave}
                disabled={isLoading}
              >
                <Save className="w-4 h-4" />
                {isLoading ? "Saving..." : "Save Changes"}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Which join page visitors see. Only exists once full customization
          has been enabled for the brand; switching either way is lossless. */}
      {customPageEnabled && (
        <section className="border border-border rounded-xl bg-card px-5 py-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="font-semibold text-foreground">Live join page</h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                {customPageLive
                  ? "Visitors see your custom page. Switching to Standard keeps every published version."
                  : customJoinPage?.publishedVersion
                    ? `Visitors see the standard page below. Your custom page (v${customJoinPage.publishedVersion}) is ready to switch to.`
                    : "Visitors see the standard page below. Publish a custom page from the Custom page tab to switch."}
              </p>
            </div>
            <div
              role="radiogroup"
              aria-label="Live join page"
              className="inline-flex rounded-lg border border-border overflow-hidden"
            >
              {(
                [
                  [false, "Standard"],
                  [true, "Custom page"],
                ] as const
              ).map(([live, label]) => (
                <button
                  key={label}
                  type="button"
                  role="radio"
                  aria-checked={customPageLive === live}
                  disabled={switching}
                  onClick={() => {
                    if (customPageLive === live) return;
                    if (live && !customJoinPage?.publishedVersion) {
                      toast("Publish your custom page first — opening the editor.");
                      navigate("/brand/settings?tab=custom-page");
                      return;
                    }
                    setPendingLive(live);
                  }}
                  className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                    customPageLive === live
                      ? "bg-brand-600 text-white"
                      : "bg-card text-muted-foreground hover:bg-muted"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <ConfirmationModal
            isOpen={pendingLive !== null}
            onClose={() => setPendingLive(null)}
            onConfirm={applyLive}
            isLoading={switching}
            variant="info"
            title={pendingLive ? "Switch to your custom page?" : "Switch to the standard page?"}
            message={
              pendingLive
                ? "This replaces your current join page immediately with the last published version of your custom page."
                : "Visitors will see the standard join page again immediately. Your custom page and its versions are kept."
            }
            itemName={customJoinPage?.publicUrl ?? undefined}
            confirmText="Switch"
          />
        </section>
      )}

      {/* Section 1: White-label (brand color customizations) */}
      <section className="border border-border rounded-xl bg-card">
        <button
          type="button"
          onClick={() => setWhiteLabelOpen((o) => !o)}
          className="w-full flex items-center justify-between px-5 py-4 text-left"
        >
          <div>
            <h3 className="font-semibold text-foreground flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-gradient-to-r from-brand-500 to-brand-700" />
              White-label
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Your brand colors, applied to your join pages and portal accents
            </p>
          </div>
          <ChevronDown
            className={`w-4 h-4 text-muted-foreground transition-transform ${
              whiteLabelOpen ? "rotate-180" : ""
            }`}
          />
        </button>
        {whiteLabelOpen && (
          <div className="px-5 pb-5 space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div id="AS-primary-color">
                <Label htmlFor="primayColour">Primary Color</Label>
                <div className="flex items-center gap-3 mt-2">
                  <input
                    type="color"
                    id="primayColour"
                    value={settings.primayColour}
                    onChange={(e) =>
                      handleSettingChange("primayColour", e.target.value)
                    }
                    className="w-12 h-12 rounded-lg border border-border cursor-pointer"
                  />
                  <Input
                    value={settings.primayColour}
                    onChange={(e) =>
                      handleSettingChange("primayColour", e.target.value)
                    }
                    placeholder="#7C3AED"
                    className="flex-1"
                  />
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Used for buttons, links, and primary elements
                </p>
              </div>

              <div id="AS-secondary-color">
                <Label htmlFor="secondaryColour">Secondary Color</Label>
                <div className="flex items-center gap-3 mt-2">
                  <input
                    type="color"
                    id="secondaryColour"
                    value={settings.secondaryColour}
                    onChange={(e) =>
                      handleSettingChange("secondaryColour", e.target.value)
                    }
                    className="w-12 h-12 rounded-lg border border-border cursor-pointer"
                  />
                  <Input
                    value={settings.secondaryColour}
                    onChange={(e) =>
                      handleSettingChange("secondaryColour", e.target.value)
                    }
                    placeholder="#E8E9FF"
                    className="flex-1"
                  />
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Used for backgrounds, borders, and secondary elements
                </p>
              </div>
            </div>

            {/* Color Preview */}
            <div className="p-4 border rounded-lg bg-muted">
              <h4 className="font-medium mb-3">Color Preview</h4>
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <div
                    className="w-6 h-6 rounded border border-border"
                    style={{ backgroundColor: settings.primayColour }}
                  />
                  <span className="text-sm text-muted-foreground">
                    Primary: {settings.primayColour}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div
                    className="w-6 h-6 rounded border border-border"
                    style={{ backgroundColor: settings.secondaryColour }}
                  />
                  <span className="text-sm text-muted-foreground">
                    Secondary: {settings.secondaryColour}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Section 2: Portal branding (white-label name + logo, ticket 7).
          Self-contained: it saves to /white-label/me, not the appearance PUT
          behind this tab's Save button. */}
      <PortalBrandingSection />

      {/* Section 3: Join portal customization — steps aside while the custom
          page is live (it's authored in HTML/CSS then), but the join copy stays
          editable because the custom page reads it through tokens. */}
      {customPageLive ? (
        <section className="border border-border rounded-xl bg-card px-5 py-4 space-y-4">
          <div>
            <h3 className="font-semibold text-foreground flex items-center gap-2">
              <Type className="w-4 h-4" />
              Join portal customization
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Your custom page is live, so the standard theme, banner framing and
              showcase settings don't apply right now — switch to Standard above
              to edit them. Your custom page receives the banner exactly as
              uploaded, and the join copy below still feeds its tokens.
            </p>
          </div>
          <CtaContentCard
            settings={settings}
            setSettings={setSettings}
            joinTextEditorRef={joinTextEditorRef}
            ambassadorTextEditorRef={ambassadorTextEditorRef}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/brand/settings?tab=custom-page")}
          >
            Open Custom page
          </Button>
        </section>
      ) : (
      <section className="border border-border rounded-xl bg-card">
        <button
          type="button"
          onClick={() => setJoinPortalOpen((o) => !o)}
          className="w-full flex items-center justify-between px-5 py-4 text-left"
        >
          <div>
            <h3 className="font-semibold text-foreground flex items-center gap-2">
              <Type className="w-4 h-4" />
              Join portal customization
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Theme, content and showcase settings for your join page
            </p>
          </div>
          <ChevronDown
            className={`w-4 h-4 text-muted-foreground transition-transform ${
              joinPortalOpen ? "rotate-180" : ""
            }`}
          />
        </button>
        {joinPortalOpen && (
          <div className="px-5 pb-5">
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Left Column - Settings */}
        <div
          className={
            viewMode === "mobile"
              ? "lg:col-span-2"
              : "lg:col-span-1 py-4 space-y-6 relative overflow-y-auto"
          }
        >
          {/* Theme Selection */}
          <Card id="AS-theme-selection">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Type className="w-5 h-5" />
                Theme Selection
              </CardTitle>
              <CardDescription className="text-xs">
                Choose from predefined theme styles that affect the overall look
                and feel
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div>
                  <Label htmlFor="theme">Theme Style</Label>
                  <Select
                    value={settings.theme}
                    onValueChange={(value) =>
                      handleSettingChange("theme", value)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(THEME_CONFIGS).map(([key, theme]) => (
                        <SelectItem key={key} value={key}>
                          {theme.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Theme Preview */}
                <div className="">
                  <h4 className="text-sm leading-tight text-muted-foreground mb-3">
                    Selected Theme: {currentTheme.name}
                  </h4>
                  <p className="text-xs text-muted-foreground">
                    {currentTheme.description}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Banner framing: saves on its own (uploads files), not via Save */}
          <BannerFramingCard />

          {/* Social Media Icon Size */}
          <Card id="AS-social-media-icon-size">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Social Media Icons
              </CardTitle>
              <CardDescription className="text-xs">
                Choose the size of social media icons displayed on your brand
                details page
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div>
                <Label htmlFor="socialMediaIconSizes">Icon Size</Label>
                <Select
                  value={settings.socialMediaIconSizes}
                  onValueChange={(value) => {
                    handleSettingChange("socialMediaIconSizes", value);
                  }}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ICON_SIZES.map((size) => (
                      <SelectItem
                        key={size.value}
                        value={size.label.toUpperCase()}
                      >
                        <div className="flex items-center gap-2">
                          <span>{size.label}</span>
                          <div
                            className={`${size.size} rounded`}
                            style={{ backgroundColor: settings.primayColour }}
                          />
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* CTA Content */}
          <CtaContentCard
            settings={settings}
            setSettings={setSettings}
            joinTextEditorRef={joinTextEditorRef}
            ambassadorTextEditorRef={ambassadorTextEditorRef}
          />
        </div>

        {/* Right Column - Preview */}
        <div
          className={viewMode === "mobile" ? "lg:col-span-2" : "lg:col-span-3"}
        >
          {showPreview ? (
            <Card
              className={viewMode === "mobile" ? "p-0" : "p-0 mt-4"}
              id="AS-preview-section"
            >
              <CardContent className="!p-0">
                {/* View Toggle Buttons */}
                <div className="flex items-center justify-end gap-2 p-4 border-b bg-muted">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setViewMode("mobile")}
                    className="flex items-center gap-2 bg-transparent hover:bg-transparent"
                    style={{
                      borderColor:
                        viewMode === "mobile"
                          ? "rgb(var(--brand-600))"
                          : "hsl(var(--border))",
                      color:
                        viewMode === "mobile"
                          ? "rgb(var(--brand-600))"
                          : "hsl(var(--muted-foreground))",
                    }}
                  >
                    <Smartphone className="w-4 h-4" />
                    Mobile
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setViewMode("desktop")}
                    className="flex items-center gap-2 bg-transparent hover:bg-transparent"
                    style={{
                      borderColor:
                        viewMode === "desktop"
                          ? "rgb(var(--brand-600))"
                          : "hsl(var(--border))",
                      color:
                        viewMode === "desktop"
                          ? "rgb(var(--brand-600))"
                          : "hsl(var(--muted-foreground))",
                    }}
                  >
                    <Monitor className="w-4 h-4" />
                    Desktop
                  </Button>
                </div>

                {/* Preview Content - Both rendered, CSS controls visibility */}
                {/* Mobile Preview */}
                <div
                  className={`flex items-center justify-center p-8 bg-gradient-to-br from-muted via-background to-muted relative overflow-hidden ${
                    viewMode === "mobile" ? "block" : "hidden"
                  }`}
                  style={{ minHeight: "calc(100vh - 300px)" }}
                >
                  {/* Background Pattern for Depth */}
                  <div
                    className="absolute inset-0 opacity-30"
                    style={{
                      backgroundImage:
                        "radial-gradient(circle at 20% 50%, rgba(0,0,0,0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(0,0,0,0.1) 0%, transparent 50%)",
                    }}
                  />

                  {/* iPhone Frame */}
                  <div className="relative z-10">
                    {/* Phone Frame - 3D Effect */}
                    <div
                      className="relative"
                      style={{
                        width: "330px",
                        height: "calc(100vh - 300px)",
                        maxHeight: "912px",
                        minHeight: "600px",
                        perspective: "1200px",
                      }}
                    >
                      <div
                        className="absolute inset-0 rounded-[3rem] shadow-[0_20px_60px_rgba(0,0,0,0.4),0_0_0_1px_rgba(255,255,255,0.1)_inset]"
                        style={{
                          background:
                            "linear-gradient(145deg, #1a1a1a 0%, #2d2d2d 50%, #1a1a1a 100%)",
                          padding: "10px",
                          transformStyle: "preserve-3d",
                          transition: "transform 0.3s ease",
                        }}
                      >
                        {/* Phone Bezel with Shine */}
                        <div
                          className="w-full h-full rounded-[2.5rem] overflow-hidden relative"
                          style={{
                            background: "#000",
                            boxShadow:
                              "inset 0 0 30px rgba(0,0,0,0.8), inset 0 2px 4px rgba(255,255,255,0.1), 0 0 50px rgba(0,0,0,0.5)",
                          }}
                        >
                          {/* Notch */}
                          <div
                            className="absolute top-0 left-1/2 transform -translate-x-1/2 z-20"
                            style={{
                              width: "150px",
                              height: "30px",
                              background: "#000",
                              borderBottomLeftRadius: "20px",
                              borderBottomRightRadius: "20px",
                              boxShadow: "0 2px 4px rgba(0,0,0,0.3)",
                            }}
                          />

                          {/* Screen Content - Iframe. Not rendered until the
                              saved settings have produced a URL: an empty src
                              would load the settings page itself recursively. */}
                          {previewUrl ? (
                            <iframe
                              key={brandDetails?.brandBannerImage ?? "no-banner"}
                              ref={mobilePreviewIframeRef}
                              src={previewUrl}
                              className="w-full h-full border-0 bg-card"
                              style={{
                                width: "100%",
                                height: "100%",
                                border: "none",
                                display: "block",
                                overflowY: "auto",
                              }}
                              title="Mobile Preview"
                            />
                          ) : (
                            <div className="w-full h-full bg-card" />
                          )}

                          {/* Home Indicator */}
                          <div
                            className="absolute bottom-2 left-1/2 transform -translate-x-1/2 z-20"
                            style={{
                              width: "134px",
                              height: "5px",
                              background: "rgba(255,255,255,0.3)",
                              borderRadius: "3px",
                              boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
                            }}
                          />
                        </div>

                        {/* Side Shine Effect */}
                        <div
                          className="absolute inset-0 rounded-[3rem] pointer-events-none"
                          style={{
                            background:
                              "linear-gradient(135deg, rgba(255,255,255,0.1) 0%, transparent 50%)",
                            mixBlendMode: "overlay",
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Desktop Preview */}
                <div
                  className={`border rounded-lg overflow-y-auto ${
                    viewMode === "desktop" ? "block" : "hidden"
                  }`}
                >
                  <BrandDetail
                    key={brandDetails?.brandBannerImage ?? "no-banner"}
                    isEmbedded={true}
                    brandId={brandDetails?.username}
                    appearanceSettings={settings}
                    changedFields={changedFields}
                  />
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="h-fit">
              <CardHeader>
                <CardTitle>Preview</CardTitle>
                <CardDescription className="text-xs">
                  Click "Show Preview" to see your changes in real-time
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-12 text-muted-foreground">
                  <Eye className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Preview will appear here when enabled</p>
                </div>
              </CardContent>
            </Card>
          )}
          <div className="space-y-4 flex flex-col items-start justify-center gap-4">
            <div className="flex-1" id="AS-showcase-images">
              <ShowcaseImagesManager
                images={settings.imageIds.map((id) => ({
                  id,
                  url: id, // For now, using the ID as URL since we're storing public URLs
                }))}
                onChange={(images) => {
                  const imageIds = images.map((img) => img.url);
                  setSettings((prev) => ({
                    ...prev,
                    imageIds: imageIds,
                  }));
                }}
                maxImages={5}
              />
            </div>

            {/* Product Showcase Images */}
            <div className="flex-1" id="AS-product-showcase">
              <ProductImageSelector
                products={products}
                selectedProductIds={settings.showcaseProductIds}
                onChange={(productIds) => {
                  setSettings((prev) => ({
                    ...prev,
                    showcaseProductIds: productIds,
                  }));
                }}
                maxSelections={4}
              />
            </div>
          </div>
        </div>
      </div>
          </div>
        )}
      </section>
      )}
    </div>
  );
}

/**
 * The brand-wide join copy (title, text, ambassador welcome). Shared between
 * the full join-portal editor and the slimmed-down view shown while the
 * custom page is live, since `{{program.joinTitle}}` / `{{program.joinText}}`
 * come from here either way.
 */
function CtaContentCard({
  settings,
  setSettings,
  joinTextEditorRef,
  ambassadorTextEditorRef,
}: {
  settings: AppearanceSettings;
  setSettings: Dispatch<SetStateAction<AppearanceSettings>>;
  joinTextEditorRef: RefObject<RichTextEditorRef>;
  ambassadorTextEditorRef: RefObject<RichTextEditorRef>;
}) {
  return (
    <Card className="mt-4" id="AS-cta-content">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="w-5 h-5" />
          CTA Content
        </CardTitle>
        <CardDescription className="text-xs">
          Customize the text that appears when affiliates want to join
          your brand. This is your brand default — every program uses it
          unless you customize that program's own join page content.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div id="AS-join-title-field">
          <Label htmlFor="joinTitle">Join Title</Label>
          <input
            type="text"
            id="joinTitle"
            value={settings.joinTitle ?? ""}
            onChange={(e) =>
              setSettings({
                ...settings,
                joinTitle: e.target.value,
              })
            }
            placeholder="Enter the title for your join section"
            className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 mt-2"
          />
        </div>
        <Separator />
        <div id="AS-join-text-field">
        <div className="flex items-center gap-2">
          <Label htmlFor="joinText">Join Text</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Info className="w-3 h-3 text-muted-foreground" />
            </PopoverTrigger>
            <PopoverContent className="w-full !p-2">
              <p className="text-sm text-muted-foreground">This is the text that appears when affiliates want to join your brand.</p>
              <ul className="text-sm text-muted-foreground list-disc list-inside">
                <li>This is your CTA for affiliates to join your brand.</li>
                <li>May Include phrases like "be part of our brand", "join now".</li>
                <li>Using the right phrasing can increase your conversion rate.</li>
                <li>Add YouTube videos urls to increase conversion.</li>
              </ul>
              </PopoverContent>
          </Popover>
          </div>
          <div className="mt-2">
            <RichTextEditor
              content={
                settings.joinText ??
                "Become part of us. Get paid for your hard work."
              }
              onChange={(content) =>
                setSettings({
                  ...settings,
                  joinText: content,
                })
              }
              placeholder="Enter the text for your joined affiliates text"
              ref={joinTextEditorRef}
              maxLength={4000}
            />
          </div>
        </div>

        <Separator />

        <div id="AS-ambassador-text-field">
          <div className="flex items-center gap-2">
          <Label htmlFor="ambassadorText">Ambassador Description</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Info className="w-3 h-3 text-muted-foreground" />
            </PopoverTrigger>
            <PopoverContent className="w-full !p-2">
              <p className="text-sm text-muted-foreground">This is the welcome section for new affiliates after they joined your program.</p>
              <ul className="text-sm text-muted-foreground list-disc list-inside">
                <li>Affiliates part of your program can only see this message.</li>
                <li>May Include welcome wishes, introductions, or next steps.</li>
                <li>Add YouTube videos urls for better Clarification.</li>
              </ul>
              </PopoverContent>
          </Popover>
          </div>
          <div className="mt-2">
            <RichTextEditor
              content={settings.ambassadorText ?? "welcome to our brand"}
              onChange={(content) =>
                setSettings({
                  ...settings,
                  ambassadorText: content,
                })
              }
              placeholder="Enter a description for potential ambassadors. You can include YouTube links and formatting."
              ref={ambassadorTextEditorRef}
              maxLength={4000}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
